import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { RoundAddIcon } from '../../../../web/components/svgs/icons/RoundAddIcon';
import { Button } from '../../../../web/components-v2/Button';
import { DeleteConfirmDialog } from '../../../../web/components-v2/DeleteConfirmDialog';
import { Input } from '../../../../web/components-v2/Input';
import { Activity, ActivityListHeader } from '../../../components/gamification/Activity';
import { Meme } from '../../../components/gamification/Meme';
import { useResponseHandler } from '../../../hooks/error';
import { ActivitiesViewModel } from '../../../viewModels/memes';
import { BasicPage } from '../../BasicPage';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
}

const getDeleteDescription = (activityCount: number) =>
	activityCount > 1
		? `This meme belongs to ${activityCount} activities. Deleting it removes it from all of them.`
		: 'This permanently deletes the meme.';

export const Gamification: React.FC<IProps> = observer(({ className = '' }) => {
	const userSession = useUserSession();
	const activities = useRef(new ActivitiesViewModel(userSession)).current;

	const [selectedActivityId, setSelectedActivityId] = useState<string>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const [memeToDelete, setMemeToDelete] = useState<string>(null);
	const [activitiesErrorHandler] = useResponseHandler('LoadActivities-Error', 'Could not load activities');
	const [memesResponseHandler] = useResponseHandler('LoadMemes-Error', 'Could not load memes');
	const [memeCreateResponseHandler] = useResponseHandler('CreateMeme-Error', 'Could not create meme');
	const [memeDeleteResponseHandler] = useResponseHandler('DeleteMeme-Error', 'Could not delete meme');

	useEffect(() => {
		activities.load().catch(activitiesErrorHandler);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (activities.activities.length && !selectedActivityId) {
			setSelectedActivityId(activities.activities[0]?.id);

			setMemeToDelete(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activities.activities?.length]);

	const onActivityClick = (activityId: string) => () => {
		setSelectedActivityId(activityId);

		setMemeToDelete(null);
	};

	const selectedActivity = useMemo(
		() => activities.activities.find(x => x.id === selectedActivityId),
		[selectedActivityId, activities]
	);

	useEffect(() => {
		if (selectedActivity) {
			selectedActivity.memeVms = [];
			selectedActivity.loadMemes().catch(memesResponseHandler);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedActivityId]);

	const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.currentTarget.files[0];
		activities.createMeme(selectedActivityId, file).catch(memeCreateResponseHandler);
	};

	const onUploadClick = () => {
		inputRef.current?.click();
	};

	const onDeleteClick = (id: string) => () => {
		setMemeToDelete(id);
	};

	const onDeleteCancel = () => {
		setMemeToDelete(null);
	};

	const memeBeingDeleted = selectedActivity?.memeVms.find(x => x.id === memeToDelete);
	const deleteDescription = getDeleteDescription(memeBeingDeleted?.activities?.length ?? 0);

	const onDeleteConfirm = () => {
		const activity = selectedActivity;
		const meme = memeBeingDeleted;
		setMemeToDelete(null);
		if (!activity || !meme) return;
		meme
			.delete()
			.then(() => {
				activity.memeVms = activity.memeVms.filter(x => x.id !== meme.id);
				activity.memes -= 1;
			})
			.catch(memeDeleteResponseHandler);
	};

	return (
		<BasicPage appearance='light' className={className} title='Gamification'>
			{activities.isBusy ? (
				<LoadingSpinner type='login' />
			) : (
				<div className={css(styleSheet.gamification)}>
					<div className={css(styleSheet.activities)}>
						<ActivityListHeader />
						{activities.activities.map(x => {
							return (
								<Activity
									key={x.id}
									activity={x}
									onClick={onActivityClick(x.id)}
									selected={x.id === selectedActivityId}
								/>
							);
						})}
					</div>
					<div className={css(styleSheet.memes)}>
						{!!selectedActivity &&
							(selectedActivity.isBusy ? (
								<LoadingSpinner type='large' />
							) : (
								<>
									{selectedActivity.memeVms.map(x => {
										return <Meme key={x.id} meme={x} onDeleteClick={onDeleteClick} />;
									})}
									<div>
										<Button variant='ghost' onClick={onUploadClick}>
											<RoundAddIcon fillColor='currentColor' />
											Add a meme
										</Button>
									</div>
								</>
							))}
					</div>
				</div>
			)}
			<Input
				aria-label='Meme file'
				styles={[styleSheet.fileInput]}
				type='file'
				onChange={onFileChange}
				ref={inputRef}
				accept='image/png, image/jpeg, image/gif, video/mp4'
			/>
			<DeleteConfirmDialog
				open={memeToDelete !== null}
				title='Are you sure you want to delete this meme?'
				description={deleteDescription}
				onClose={onDeleteCancel}
				onConfirm={onDeleteConfirm}
			/>
		</BasicPage>
	);
});
